import { useEffect, useCallback } from "react";
import { Mail, MailThread } from "./types";

interface UseKeyboardShortcutsProps {
  mails: Mail[];
  threads?: MailThread[];
  selectedMail: Mail | null;
  selectedThread?: MailThread | null;
  onSelectMail: (mail: Mail | null) => void;
  onSelectThread?: (thread: MailThread | null) => void;
  onReply: (mail: Mail) => void;
  onArchive: (mailId: string) => void;
  onCompose: () => void;
  onRefresh: () => void;
  useThreads?: boolean;
}

export function useKeyboardShortcuts({
  mails,
  threads,
  selectedMail,
  selectedThread,
  onSelectMail,
  onSelectThread,
  onReply,
  onArchive,
  onCompose,
  onRefresh,
  useThreads = false,
}: UseKeyboardShortcutsProps) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      const items = useThreads ? threads || [] : mails;
      const currentIndex = useThreads
        ? threads?.findIndex((t) => t.id === selectedThread?.id) ?? -1
        : mails.findIndex((m) => m.id === selectedMail?.id);

      switch (event.key) {
        // Navigation
        case "j":
        case "ArrowDown":
          event.preventDefault();
          if (items.length > 0) {
            const nextIndex = Math.min(currentIndex + 1, items.length - 1);
            if (nextIndex !== currentIndex || currentIndex === -1) {
              if (useThreads && onSelectThread && threads) {
                onSelectThread(threads[nextIndex === -1 ? 0 : nextIndex]);
              } else {
                onSelectMail(mails[nextIndex === -1 ? 0 : nextIndex]);
              }
            }
          }
          break;

        case "k":
        case "ArrowUp":
          event.preventDefault();
          if (items.length > 0 && currentIndex > 0) {
            if (useThreads && onSelectThread && threads) {
              onSelectThread(threads[currentIndex - 1]);
            } else {
              onSelectMail(mails[currentIndex - 1]);
            }
          }
          break;

        // Open/Select
        case "Enter":
        case "o":
          if (currentIndex >= 0) {
            event.preventDefault();
            // Already selected, could open full view in future
          }
          break;

        // Reply
        case "r":
          if (selectedMail) {
            event.preventDefault();
            onReply(selectedMail);
          }
          break;

        // Archive
        case "e":
          if (selectedMail) {
            event.preventDefault();
            onArchive(selectedMail.id);
          }
          break;

        // Compose
        case "c":
          event.preventDefault();
          onCompose();
          break;

        // Refresh
        case "u":
          event.preventDefault();
          onRefresh();
          break;

        // Escape - deselect
        case "Escape":
          event.preventDefault();
          onSelectMail(null);
          if (onSelectThread) {
            onSelectThread(null);
          }
          break;

        // Go to first
        case "Home":
          event.preventDefault();
          if (items.length > 0) {
            if (useThreads && onSelectThread && threads) {
              onSelectThread(threads[0]);
            } else {
              onSelectMail(mails[0]);
            }
          }
          break;

        // Go to last
        case "End":
          event.preventDefault();
          if (items.length > 0) {
            if (useThreads && onSelectThread && threads) {
              onSelectThread(threads[threads.length - 1]);
            } else {
              onSelectMail(mails[mails.length - 1]);
            }
          }
          break;
      }
    },
    [
      mails,
      threads,
      selectedMail,
      selectedThread,
      onSelectMail,
      onSelectThread,
      onReply,
      onArchive,
      onCompose,
      onRefresh,
      useThreads,
    ]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);
}

// Keyboard shortcuts help text
export const keyboardShortcuts = [
  { key: "j / ↓", description: "Next email" },
  { key: "k / ↑", description: "Previous email" },
  { key: "r", description: "Reply" },
  { key: "e", description: "Archive" },
  { key: "c", description: "Compose" },
  { key: "u", description: "Refresh" },
  { key: "Esc", description: "Deselect" },
  { key: "Home", description: "First email" },
  { key: "End", description: "Last email" },
];
